// Unpublished Work © 2023-2026 Deere & Company.

import React from 'react';
import PropTypes from 'Utils/prop-type-utils';
import {connect} from 'react-redux';
import {getModelMaintenances} from 'Services/fleet-service';
import {deleteModelMaintenance} from 'Services/model-maintenance-service';
import {fetchEffectData, useDeepEffect} from 'Utils/react-utils';
import {replaceTranslationNames} from 'Utils/translation-utils';
import {TOAST_TYPE} from 'Src/ui-core/constants/toast-constants';
import {useToast} from 'Src/ui-core/react-hooks/use-toast';
import {closeDialog, openDialog} from 'Store/actions/dialogs';
import dialogTypes from 'Ui/components/common/dialog-types';
import {
    alphaNumericCompare,
    genericServiceCompare,
    getInterval
} from 'Ui/models/maintenance';
import AddButton from 'Ui/components/settings/common/add-button';
import EditLink from 'Ui/components/common/data-table/edit-link';
import {isAuthorized} from 'Common/utils/authorization-handler';
import {MANAGE_EQUIPMENT_SETTINGS} from 'Common/constants/business-activities';
import {MAINTENANCE_MANAGER_PROGOLF} from 'Common/constants/isg-toggles';
import DataGridWrapper from 'Src/ui-core/components/common/data-table/data-grid-wrapper';

function getDataGridColumns(translations, openAddEditMaintenanceDialog, fetchModelMaintenances, isFleetToMaintenanceEnabled) {
    return [
        {
            headerName: translations.MODEL,
            field: 'modelName',
            flex: 1,
            renderCell: (params) => {
                const {row} = params;

                if (isFleetToMaintenanceEnabled) {
                    return row.modelName;
                }

                return (
                    <EditLink
                        onClick={() => {
                            openAddEditMaintenanceDialog({
                                initialModelMaintenanceId: row.modelMaintenanceId,
                                translations,
                                onClose: fetchModelMaintenances
                            });
                        }}
                    >
                        {row.modelName}
                    </EditLink>
                );
            }
        },
        {
            headerName: translations.BRAND,
            flex: 1,
            field: 'manufacturerName'
        },
        {
            headerName: translations.ONLINK_DETAIL,
            width: 200,
            field: 'description'
        },
        {
            headerName: translations.ONLINK_SERVICE_TYPE,
            flex: 1,
            field: 'serviceTypeName'
        },
        {
            headerName: translations.INTERVAL,
            field: 'interval',
            flex: 1,
            valueGetter: (_value, row) => row,
            sortComparator: (a, b) => {
                return genericServiceCompare(a, b, alphaNumericCompare, 'interval');
            },
            renderCell: (params) => params.row.interval
        }
    ];
}

function MaintenanceSettings(props) {
    const {
        closeConfirmation,
        fleetId,
        isgToggles,
        openAddEditMaintenanceDialog,
        openConfirmation,
        translations
    } = props;

    const isFleetToMaintenanceEnabled = isgToggles?.[MAINTENANCE_MANAGER_PROGOLF];
    const {addToast} = useToast();
    const [loading, setLoading] = React.useState(true);
    const [modelMaintenances, setModelMaintenances] = React.useState([]);

    async function fetchModelMaintenances(isMounted = () => true) {
        setLoading(true);

        const {modelMaintenance} = await getModelMaintenances(fleetId);

        const formattedMaintenance = modelMaintenance.map((maintenanceSchedule) => ({
            ...maintenanceSchedule,
            interval: getInterval(maintenanceSchedule, translations)
        }));

        if (isMounted()) {
            setModelMaintenances(formattedMaintenance);
            setLoading(false);
        }
    }

    function deleteModelMaintenanceHandler(modelMaintenance) {
        openConfirmation({
            message: replaceTranslationNames(translations.ONLINK_ITEM_THIS_CANNOT_BE_UNDONE, {
                '0': translations.ONLINK_MAINTENANCE_SCHEDULE.toLowerCase()
            }),
            onCancel: closeConfirmation,
            showLoadingIcon: true,
            title: replaceTranslationNames(translations.ONLINK_DELETE_ITEM, {
                '0': translations.ONLINK_MAINTENANCE_SCHEDULE
            }),
            async onContinue() {
                try {
                    await deleteModelMaintenance(modelMaintenance.modelMaintenanceId);
                } catch {
                    addToast({
                        message: replaceTranslationNames(translations.ONLINK_CONFIRM_ITEM_DELETION_FAILED, {
                            '0': translations.ONLINK_MAINTENANCE_SCHEDULE.toLowerCase()
                        }),
                        type: TOAST_TYPE.ERROR
                    });
                }

                closeConfirmation();
                fetchModelMaintenances();
            }
        });
    }

    useDeepEffect(() => fetchEffectData(fetchModelMaintenances), [fleetId]);

    const addMaintenanceButton = (
        <div className='extra-table-options'>
            <AddButton
                label={translations.ONLINK_ADD_MAINTENANCE_SCHEDULE}
                onClick={() => {
                    openAddEditMaintenanceDialog({
                        translations,
                        onClose: fetchModelMaintenances
                    });
                }}
            />
        </div>
    );

    return (
        <div
            className={'settings-container data-grid'}
            data-testid='settings-container'
        >
            <DataGridWrapper
                columns={getDataGridColumns(translations, openAddEditMaintenanceDialog, fetchModelMaintenances, isFleetToMaintenanceEnabled)}
                deleteHandler={deleteModelMaintenanceHandler}
                filterComponent={!isFleetToMaintenanceEnabled ? addMaintenanceButton : null}
                getRowId={(row) => row.modelMaintenanceId}
                initialState={{
                    sorting: {
                        sortModel: [
                            {
                                field: 'modelName',
                                sort: 'asc'
                            }
                        ]
                    }
                }}
                loading={loading}
                noDataAvailableMessage={translations.ONLINK_NO_MAINTENANCE_SCHEDULES_FOUND}
                rows={modelMaintenances}
                searchFields={['modelName', 'manufacturerName', 'description', 'serviceTypeName']}
                searchable={true}
                showDeleteColumn={isAuthorized({
                    myJdPermissions: MANAGE_EQUIPMENT_SETTINGS
                }, props) && !isFleetToMaintenanceEnabled}
                slotProps={{
                    root: {
                        disableColumnMenu: true
                    }
                }}
                translations={translations}
            />
        </div>
    );
}

MaintenanceSettings.propTypes = {
    closeConfirmation: PropTypes.func,
    fleetId: PropTypes.string,
    isgToggles: PropTypes.object,
    isMigrated: PropTypes.bool,
    myJdPermissions: PropTypes.myJdPermissions,
    openAddEditMaintenanceDialog: PropTypes.func,
    openConfirmation: PropTypes.func,
    translations: PropTypes.translations
};

export function mapStateToProps(state) {
    return {
        fleetId: state.membership.fleetId,
        isgToggles: state.account.isgToggles,
        isMigrated: state.membership.isMigrated,
        myJdPermissions: state.account.myJdPermissions,
        translations: state.translations
    };
}

export function mapDispatchToProps(dispatch) {
    return {
        closeConfirmation() {
            dispatch(closeDialog(dialogTypes.CONFIRMATION_DIALOG));
        },
        openConfirmation(props) {
            dispatch(openDialog(dialogTypes.CONFIRMATION_DIALOG, props));
        },
        openAddEditMaintenanceDialog(props) {
            dispatch(openDialog(dialogTypes.ADD_EDIT_MAINTENANCE_DIALOG, props));
        }
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(MaintenanceSettings);
